import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { InputTextarea } from 'primereact/inputtextarea'
import FormDialog from '../../../components/ui/FormDialog'
import FormField, { FormGrid } from '../../../components/ui/FormField'
import { COLUMN_BYTES } from '../../../lib/columnBytes'
import { utf8Length } from '../../../lib/textBytes'
import type { TodoTask } from '../types'

/** Yorumun ürün sınırı — backend `TodoTaskDescriptionDto` `@Size(max = 2500)` (22.10.2026). */
export const TODO_COMMENT_MAX_LENGTH = 2500

interface Props {
  /** Açık madde; `null` → pop-up kapalı. */
  task: TodoTask | null
  submitting: boolean
  /** Kırpılmış metin gelir; **boş dize yorumu kaldırır**. */
  onSubmit: (description: string) => void
  onHide: () => void
  /**
   * Kısıtlı Yetkili asistan kipi (126). `true` iken pop-up **okuma penceresine** döner: footer
   * (Kaydet) hiç çizilmez, alan salt-okunur olur ve "boşaltıp kaydet" ipucu düşer — o ipucu bir
   * YAZMA yolunu anlatıyor.
   */
  readOnly?: boolean
}

/**
 * **Notlarım maddesi — Yorum** pop-up'ı (124). Madde başına **tek** yorum: pop-up mevcut yorumla
 * açılır, Kaydet onu değiştirir; metni silip kaydetmek yorumu kaldırır (ayrı bir silme düğmesi yok —
 * tek değerli bir alanda "boşalt" ile "sil" aynı sonuçtur).
 *
 * <p>Boş pop-up yalnız **mevcut bir yorum varken** gönderilebilir (= kaldır); yorumsuz maddede boş
 * gönderim anlamsız bir istek olurdu.
 *
 * <p><b>`maxLength` KULLANILMAZ</b> — `CompletionNoteDialog` (FR-047a) ile aynı gerekçe: yapıştırılan
 * fazlalık sessizce kesilirdi. Alan yazmaya izin verir, sayaç kalanı gösterir, gönderim engellenir.
 *
 * <p><b>Bayt sınırı ayrıca denetlenir:</b> 2500 karakterin en kötü hâli 4000 baytlık kolona sığmaz
 * (çok baytlı harf 1500'ü geçerse). Sunucu `@MaxBytes` ile reddeder; burada aynı sınır gönderimden
 * önce gösterilir. Sayaç karakter sayar — olağan metinde bayt sınırı hiç devreye girmez.
 *
 * <p>Metin **yalnız açılışta** (madde kimliği değişince) doldurulur: 409 sonrası liste tazelenince
 * aynı madde yeni bir nesneyle gelir; o an metni sıfırlamak kullanıcının yazdığını silerdi.
 */
export default function TodoTaskCommentDialog({
  task,
  submitting,
  onSubmit,
  onHide,
  readOnly = false,
}: Readonly<Props>) {
  const { t } = useTranslation()
  const taskId = task?.id ?? null
  const [text, setText] = useState(task?.description ?? '')
  // Render sırasında "prop değişince state'i ayarla" (React önerisi; effect'e gerek yok). Yalnız
  // KİMLİK değişince doldurulur — bkz. bileşen javadoc'u.
  const [openedFor, setOpenedFor] = useState(taskId)
  // Başlık ve konu satırı AÇILIŞTAKİ maddeden okunur: kapanırken `task` null'a düşer ve başlık
  // kapanış animasyonu sırasında "Yorumu Düzenle" → "Yorum Ekle"ye dönüp konu satırı kayboluyordu.
  const [shown, setShown] = useState(task)
  if (taskId !== openedFor) {
    setOpenedFor(taskId)
    setText(task?.description ?? '')
    if (task != null) setShown(task)
  }

  const trimmed = text.trim()
  const tooLong = trimmed.length > TODO_COMMENT_MAX_LENGTH
  const tooManyBytes = !tooLong && utf8Length(trimmed) > COLUMN_BYTES.todoTaskDescription
  const invalid = tooLong || tooManyBytes
  const hadComment = Boolean(task?.description)
  const unchanged = trimmed === (task?.description ?? '')
  const blocked = invalid || unchanged || (trimmed.length === 0 && !hadComment)

  const submit = () => {
    if (readOnly || submitting || blocked) return
    onSubmit(trimmed)
  }

  /**
   * Alanı açıklayan düğümlerin id'leri. Salt-okumada ikisi de çizilmediği için **tanımsız** kalır;
   * uzunluk sayacı ve "boşaltıp kaydet" ipucu yalnız yazarken anlamlı.
   */
  const describedBy = (() => {
    if (readOnly) return undefined
    return hadComment
      ? 'todo-comment-counter todo-comment-hint todo-comment-err'
      : 'todo-comment-counter todo-comment-err'
  })()

  const fieldError = (() => {
    if (tooLong) return t('todo.comment.tooLong', { max: TODO_COMMENT_MAX_LENGTH })
    if (tooManyBytes) return t('validation.maxBytes')
    return undefined
  })()

  /** Salt-okumada başlık "Yorum"; "Yorumu Düzenle" diyen bir pencere düzenlemeye götürmüyor. */
  const commentHeader = (() => {
    if (readOnly) return t('todo.comment.viewTitle')
    return t(shown?.description ? 'todo.comment.editTitle' : 'todo.comment.addTitle')
  })()

  return (
    <FormDialog
      visible={task != null}
      header={commentHeader}
      hideFooter={readOnly}
      submitLabel={t('todo.save')}
      submitting={submitting}
      submitDisabled={blocked}
      onSubmit={submit}
      onHide={onHide}
    >
      {shown != null && (
        <p className="ui-dialog-subject" title={shown.name}>
          {shown.name}
        </p>
      )}
      <FormGrid>
        <FormField label={t('todo.comment.label')} htmlFor="todo-comment" error={fieldError} full>
          <InputTextarea
            id="todo-comment"
            value={text}
            onChange={(e) => setText(e.target.value)}
            readOnly={readOnly}
            rows={4}
            autoResize
            placeholder={t('todo.comment.placeholder')}
            aria-invalid={invalid}
            /* Salt-okumada sayaç da ipucu da çizilmiyor; var olmayan id'lere işaret eden bir
               `aria-describedby` ekran okuyucuda sessizce hiçbir şey okutmaz. */
            aria-describedby={describedBy}
          />
          {/* Sayaç canlı DEĞİL (her tuşta duyurmak böler); eşik aşımını FormField'ın hata bölgesi duyurur.
              Salt-okumada hiç çizilmez: değiştirilemeyen bir metnin uzunluk sayacı bilgi taşımaz. */}
          {!readOnly && (
            <small
              id="todo-comment-counter"
              className={
                invalid ? 'todo-comment-counter todo-comment-counter--over' : 'todo-comment-counter'
              }
            >
              {trimmed.length} / {TODO_COMMENT_MAX_LENGTH}
            </small>
          )}
          {/* Silmenin yolu görünmez olmasın: ayrı düğme yok, metni boşaltıp kaydetmek kaldırır. */}
          {hadComment && !readOnly && (
            <small id="todo-comment-hint" className="todo-comment-hint">
              {t('todo.comment.clearHint')}
            </small>
          )}
        </FormField>
      </FormGrid>
    </FormDialog>
  )
}
